// ========== COMPLETE ENHANCED APP.JSX WITH APPLE-STYLE DESIGN ==========
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Link, NavLink, useLocation, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination, EffectFade, EffectCoverflow, EffectCreative } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import 'swiper/css/effect-coverflow';
import 'swiper/css/effect-creative';
import ResendActivation from './components/ResendActivation';
import ParentApprovalDashboard from './components/ParentApprovalDashboard';
import VisitorCounter from './components/VisitorCounter';
 import Election2026 from './pages/Election2026';
import ApprovedBookingsAudit from './pages/ApprovedBookingsAudit';

import './App.css';

// ========== IMAGE PATH UTILITY ==========
import { getImagePath } from './utils/imagePath';

// ========== API SERVICES ==========
import { 
  authAPI, 
  memberAPI, 
  eventAPI, 
  statsAPI, 
  announcementAPI,
  activityAPI,
  testConnection,
  testDatabase,
  testAllAPIs,
  quickTest,
  checkApiStatus,
  initializeAuth 
} from './services/api';
import api from './services/api';
import { galleryService } from './services/galleryService';

// ========== PAGE IMPORTS ==========
import AdminApprovalPage from './pages/AdminApprovalPage'; 
import { facilities, sections, equipmentCatalog } from './utils/constants';

import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import BookingPage from './pages/BookingPage';
import ProtectedRoute from './components/ProtectedRoute';

// ========== IMPORT NEW PAGES ==========
import FacilitiesPage from './pages/FacilitiesPage';
import SectionsPage from './pages/SectionsPage';
import EquipmentPage from './pages/EquipmentPage';
import MyBookingsPage from './pages/MyBookingsPage';
import BookingDetailPage from './pages/BookingDetailPage';
import NotificationsPage from './pages/NotificationsPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';

// ========== IMPORT CLUB PAGE COMPONENTS ==========
import OurStory from './components/club/OurStory';
import Leadership from './components/club/Leadership';
import MembershipEligibility from './components/club/MembershipEligibility';
import ClubHistory from './components/club/ClubHistory';
import Awards from './components/club/Awards';
import Policies from './components/club/Policies';

// ========== IMPORT FACILITIES & SECTIONS COMPONENTS ==========
import FacilitiesOverview from './components/facilities/FacilitiesOverview';
import FacilityDetail from './components/facilities/FacilityDetail';
import SectionsOverview from './components/sections/SectionsOverview';

// ========== IMPORT FACILITIES PAGE COMPONENTS ==========
import AbujaBoulevard from './components/facilities/AbujaBoulevard';
import BanquetHall from './components/facilities/BanquetHall';
import ClubhouseAlcove from './components/facilities/ClubhouseAlcove';
import ClubhouseWestBar from './components/facilities/ClubhouseWestBar';
import DBNBar from './components/facilities/DBNBar';
import FinimaNaturePark from './components/facilities/FinimaNaturePark';
import GolfCourseBar from './components/facilities/GolfCourseBar';
import HippoBar from './components/facilities/HippoBar';
import MarqueeTent from './components/facilities/MarqueeTent';
import MarketSquare from './components/facilities/MarketSquare';
import SportsHallComplex from './components/facilities/SportsHallComplex';
import SwimmingPool from './components/facilities/SwimmingPool';
import TennisHut from './components/facilities/TennisHut';
import WellnessCenter from './components/facilities/WellnessCenter';
import KidsPlayArea from './components/facilities/KidsPlayArea';

// ========== IMPORT SECTIONS PAGE COMPONENTS ==========
import AthleticsAerobics from './components/sections/AthleticsAerobics';
import Badminton from './components/sections/Badminton';
import BalletGymnastics from './components/sections/BalletGymnastics';
import Basketball from './components/sections/Basketball';
import Chess from './components/sections/Chess';
import Football from './components/sections/Football';
import Golf from './components/sections/Golf';
import Karate from './components/sections/Karate';
import PoolSnooker from './components/sections/PoolSnooker';
import RALA from './components/sections/RALA';
import Scrabble from './components/sections/Scrabble';
import Squash from './components/sections/Squash';
import Swimming from './components/sections/Swimming';
import TableTennis from './components/sections/TableTennis';
import Tennis from './components/sections/Tennis';
import Volleyball from './components/sections/Volleyball';
import Yoga from './components/sections/Yoga';
import Cycling from './components/sections/Cycling';
import GalleryPage from './components/GalleryPage';

// ========== IMPORT EVENT PAGES ==========
import UpcomingEvents from './pages/events/UpcomingEvents';
import EventCalendar from './pages/events/EventCalendar';
import PhotoGallery from './pages/events/PhotoGallery';
import PastEvents from './pages/events/PastEvents';
import ContactPage from './pages/ContactPage';

// ========== IMPORT EVENT SERVICE ==========
import { eventService } from './services/eventService';

// ========== CONTEXT & UTILITIES ==========
const AppContext = React.createContext();

const scrollToSection = (sectionId) => {
  const section = document.getElementById(sectionId);
  if (section) {
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const FadeInSection = ({ children, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

    const { current } = domRef;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
    };
  }, []);

  return (
    <motion.div
      ref={domRef}
      initial={{ opacity: 0, y: 30 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8, delay: delay * 0.2, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
};
// ========== NAVBAR WITH DROPDOWNS - APPLE STYLE ==========
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const timeoutRef = useRef();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const toggleDropdown = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const handleMouseEnter = (dropdown) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveDropdown(dropdown);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 200);
  };

  const clubDropdown = [
    { label: 'Our Story', path: '/our-story', icon: 'fa-book-open', description: 'Discover our journey since 1998' },
    { label: 'Leadership Team', path: '/leadership', icon: 'fa-users-cog', description: 'Meet our experienced leaders' },
    { label: 'Membership Eligibility', path: '/membership-eligibility', icon: 'fa-check-circle', description: 'See if you qualify' },
    { label: 'Club History', path: '/history', icon: 'fa-landmark', description: 'Our legacy of excellence' },
    { label: 'Awards & Recognition', path: '/awards', icon: 'fa-trophy', description: 'Our achievements' },
    { label: 'Rules & Policies', path: '/policies', icon: 'fa-gavel', description: 'Club guidelines' },
  ];

  const facilitiesDropdown = [
    { label: 'Abuja Boulevard', path: '/facilities/abuja-boulevard', icon: 'fa-building', category: 'Dining' },
    { label: 'Banquet Hall', path: '/facilities/banquet-hall', icon: 'fa-glass-cheers', category: 'Events' },
    { label: 'Clubhouse Alcove', path: '/facilities/clubhouse-alcove', icon: 'fa-couch', category: 'Lounge' },
    { label: 'Clubhouse West-Bar', path: '/facilities/clubhouse-west-bar', icon: 'fa-cocktail', category: 'Bar' },
    { label: 'DBN Bar', path: '/facilities/dbn-bar', icon: 'fa-wine-glass-alt', category: 'Bar' },
    { label: 'Finima Nature Park', path: '/facilities/finima-nature-park', icon: 'fa-tree', category: 'Outdoor' },
    { label: 'Golf Course Bar', path: '/facilities/golf-course-bar', icon: 'fa-golf-ball', category: 'Sports' },
    { label: 'Hippo Bar', path: '/facilities/hippo-bar', icon: 'fa-utensils', category: 'Dining' },
    { label: 'Marquee Tent', path: '/facilities/marquee-tent', icon: 'fa-campground', category: 'Events' },
    { label: 'Market Square', path: '/facilities/market-square', icon: 'fa-store', category: 'Shopping' },
    { label: 'Sports Hall Complex', path: '/facilities/sports-hall-complex', icon: 'fa-basketball-ball', category: 'Sports' },
    { label: 'Swimming Pool', path: '/facilities/swimming-pool', icon: 'fa-swimmer', category: 'Sports' },
    { label: 'Tennis Hut', path: '/facilities/tennis-hut', icon: 'fa-table-tennis', category: 'Sports' },
    { label: 'Wellness Center', path: '/facilities/wellness-center', icon: 'fa-spa', category: 'Wellness' },
    { label: 'Kids Play Area', path: '/facilities/kids-play-area', icon: 'fa-child', category: 'Family' },
  ];

  const groupedFacilities = facilitiesDropdown.reduce((acc, facility) => {
    if (!acc[facility.category]) acc[facility.category] = [];
    acc[facility.category].push(facility);
    return acc;
  }, {});

  const sectionsDropdown = [
    { label: 'Athletics/Aerobics Section', path: '/sections/aerobics', icon: 'fa-running' },
    { label: 'Badminton Section', path: '/sections/badminton', icon: 'fa-table-tennis' },
    { label: 'Ballet and Gymnastics Section', path: '/sections/ballet-gymnastics', icon: 'fa-child' },
    { label: 'Basketball Section', path: '/sections/basketball', icon: 'fa-basketball-ball' },
    { label: 'Chess Section', path: '/sections/chess', icon: 'fa-chess' },
    { label: 'Football Section', path: '/sections/football', icon: 'fa-futbol' },
    { label: 'Bonny Island Golf Club', path: '/sections/golf', icon: 'fa-golf-ball' },
    { label: 'Karate Section', path: '/sections/karate', icon: 'fa-fist-raised' },
    { label: 'Pool/Snooker Section', path: '/sections/pool-snooker', icon: 'fa-circle' },
    { label: 'RA Ladies Association', path: '/sections/rala', icon: 'fa-female' },
    { label: 'Scrabble Section', path: '/sections/scrabble', icon: 'fa-font' },
    { label: 'Squash Section', path: '/sections/squash', icon: 'fa-table-tennis' },
    { label: 'Swimming Section', path: '/sections/swimming', icon: 'fa-swimmer' },
    { label: 'Table Tennis Section', path: '/sections/table-tennis', icon: 'fa-table-tennis' },
    { label: 'Tennis Section', path: '/sections/tennis', icon: 'fa-table-tennis' },
    { label: 'Volleyball Section', path: '/sections/volleyball', icon: 'fa-volleyball-ball' },
  ];

  const eventsDropdown = [
    { label: 'Upcoming Events', path: '/events', icon: 'fa-calendar-alt' },
    { label: 'Event Calendar', path: '/calendar', icon: 'fa-calendar-week', description: 'Full schedule' },
    { label: 'Photo Gallery', path: '/gallery', icon: 'fa-images' },
    { label: 'Past Events', path: '/past-events', icon: 'fa-history', description: 'Event archives' },
  ];

  return (
    <>
      <motion.header 
        className={`navbar ${scrolled ? 'scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
      >
        <div className="container">
          <nav className="nav-container">
            <Link to="/" className="logo">
              <img src={getImagePath('images/logo.png')} alt="NLNG RA Club" className="logo-img" />
            </Link>

            <div className="nav-desktop">
              <ul className="nav-menu">
                <li className="nav-item dropdown" 
                    onMouseEnter={() => handleMouseEnter('club')}
                    onMouseLeave={handleMouseLeave}>
                  <button className={`nav-link ${activeDropdown === 'club' ? 'active' : ''}`}
                          onClick={() => toggleDropdown('club')}>
                    <span>The Club</span>
                    <i className="fas fa-chevron-down dropdown-arrow"></i>
                  </button>
                  <AnimatePresence>
                    {activeDropdown === 'club' && (
                      <motion.div className="dropdown-menu enhanced"
                                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                  animate={{ opacity: 1, y: 0, scale: 1 }}
                                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                  transition={{ duration: 0.2 }}>
                        <div className="dropdown-header">
                          <h4>The Club</h4>
                          <p>Discover our rich heritage</p>
                        </div>
                        {clubDropdown.map((item, index) => (
                          <Link key={index} to={item.path} className="dropdown-item enhanced"
                                onClick={() => setActiveDropdown(null)}>
                            <i className={`fas ${item.icon}`}></i>
                            <div className="dropdown-item-content">
                              <span className="item-title">{item.label}</span>
                              {item.description && <span className="item-description">{item.description}</span>}
                            </div>
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>

                <li className="nav-item dropdown"
                    onMouseEnter={() => handleMouseEnter('facilities')}
                    onMouseLeave={handleMouseLeave}>
                  <button className={`nav-link ${activeDropdown === 'facilities' ? 'active' : ''}`}
                          onClick={() => toggleDropdown('facilities')}>
                    <span>Facilities</span>
                    <i className="fas fa-chevron-down dropdown-arrow"></i>
                  </button>
                  <AnimatePresence>
                    {activeDropdown === 'facilities' && (
                      <motion.div className="dropdown-menu wide enhanced"
                                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                  animate={{ opacity: 1, y: 0, scale: 1 }}
                                  exit={{ opacity: 0, y: 10, scale: 0.95 }}>
                        <div className="dropdown-header">
                          <h4>Our Facilities</h4>
                          <p>World-class amenities for every interest</p>
                        </div>
                        <div className="dropdown-categories">
                          {Object.entries(groupedFacilities).map(([category, items]) => (
                            <div key={category} className="dropdown-category">
                              <h5>{category}</h5>
                              {items.map((item, index) => (
                                <Link key={index} to={item.path} className="dropdown-item"
                                      onClick={() => setActiveDropdown(null)}>
                                  <i className={`fas ${item.icon}`}></i>
                                  <span>{item.label}</span>
                                </Link>
                              ))}
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>

                <li className="nav-item dropdown"
                    onMouseEnter={() => handleMouseEnter('sections')}
                    onMouseLeave={handleMouseLeave}>
                  <button className={`nav-link ${activeDropdown === 'sections' ? 'active' : ''}`}
                          onClick={() => toggleDropdown('sections')}>
                    <span>Sections</span>
                    <i className="fas fa-chevron-down dropdown-arrow"></i>
                  </button>
                  <AnimatePresence>
                    {activeDropdown === 'sections' && (
                      <motion.div className="dropdown-menu wide enhanced"
                                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                  animate={{ opacity: 1, y: 0, scale: 1 }}
                                  exit={{ opacity: 0, y: 10, scale: 0.95 }}>
                        <div className="dropdown-header">
                          <h4>Club Sections</h4>
                          <p>Join our active communities</p>
                        </div>
                        <div className="dropdown-grid">
                          {sectionsDropdown.map((item, index) => (
                            <Link key={index} to={item.path} className="dropdown-item"
                                  onClick={() => setActiveDropdown(null)}>
                              <i className={`fas ${item.icon}`}></i>
                              <span>{item.label}</span>
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>

                <li className="nav-item dropdown"
                    onMouseEnter={() => handleMouseEnter('events')}
                    onMouseLeave={handleMouseLeave}>
                  <button className={`nav-link ${activeDropdown === 'events' ? 'active' : ''}`}
                          onClick={() => toggleDropdown('events')}>
                    <span>Events</span>
                    <i className="fas fa-chevron-down dropdown-arrow"></i>
                  </button>
                  <AnimatePresence>
                    {activeDropdown === 'events' && (
                      <motion.div className="dropdown-menu enhanced"
                                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                  animate={{ opacity: 1, y: 0, scale: 1 }}
                                  exit={{ opacity: 0, y: 10, scale: 0.95 }}>
                        <div className="dropdown-header">
                          <h4>Events & Activities</h4>
                          <p>Stay engaged with our community</p>
                        </div>
                        {eventsDropdown.map((item, index) => (
                          <Link key={index} to={item.path} className="dropdown-item enhanced"
                                onClick={() => setActiveDropdown(null)}>
                            <i className={`fas ${item.icon}`}></i>
                            <div className="dropdown-item-content">
                              <span className="item-title">{item.label}</span>
                              {item.description && <span className="item-description">{item.description}</span>}
                            </div>
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>

                <li className="nav-item">
                  <Link to="/contact" className="nav-link">Contact</Link>
                </li>
              </ul>
                            

            </div>

<div className="nav-actions">
  <Link to="/login" className="nav-link login-link">
    <i className="fas fa-user"></i>
    <span>Sign In</span>
  </Link>
<Link to="/elections" className="nav-link election-link">
    <i className="fas fa-vote-yea"></i>
    <span>Election 2026</span>
  </Link> 
</div>

            <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <div className={`menu-icon ${mobileMenuOpen ? 'open' : ''}`}>
                <span></span><span></span><span></span>
              </div>
            </button>
          </nav>
        </div>
      </motion.header>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div className="mobile-menu-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      onClick={() => setMobileMenuOpen(false)}>
            <motion.div className="mobile-menu" initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
                        transition={{ type: "spring", damping: 25 }} onClick={e => e.stopPropagation()}>
              <div className="mobile-menu-header">
                <img src={getImagePath('images/logo.png')} alt="NLNG RA Club" className="mobile-logo" />
                <button className="mobile-close" onClick={() => setMobileMenuOpen(false)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              <div className="mobile-menu-content">
<div className="mobile-user-section">
  <Link to="/login" className="mobile-user-btn" onClick={() => setMobileMenuOpen(false)}>
    <i className="fas fa-user-circle"></i>
    <span>Sign In / Register</span>
  </Link>
</div>

{/* Election button in mobile menu - with proper spacing */}
<Link 
  to="/elections" 
  className="mobile-election-btn" 
  onClick={() => setMobileMenuOpen(false)}
>
  <i className="fas fa-vote-yea"></i>
  <span>Election Season 2026</span>
  <i className="fas fa-chevron-right"></i>
</Link>

{/* Accordion items start here */}
<AccordionItem title="The Club" icon="fa-building">
                  {clubDropdown.map((item, index) => (
                    <Link key={index} to={item.path} className="mobile-submenu-item" onClick={() => setMobileMenuOpen(false)}>
                      <i className={`fas ${item.icon}`}></i>
                      <div>
                        <div>{item.label}</div>
                        {item.description && <small>{item.description}</small>}
                      </div>
                    </Link>
                  ))}
                </AccordionItem>

                <AccordionItem title="Facilities" icon="fa-swimmer">
                  {facilitiesDropdown.map((item, index) => (
                    <Link key={index} to={item.path} className="mobile-submenu-item" onClick={() => setMobileMenuOpen(false)}>
                      <i className={`fas ${item.icon}`}></i>
                      <div>
                        <div>{item.label}</div>
                        <small>{item.category}</small>
                      </div>
                    </Link>
                  ))}
                </AccordionItem>

                <AccordionItem title="Sections" icon="fa-running">
                  {sectionsDropdown.map((item, index) => (
                    <Link key={index} to={item.path} className="mobile-submenu-item" onClick={() => setMobileMenuOpen(false)}>
                      <i className={`fas ${item.icon}`}></i>
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </AccordionItem>

                <AccordionItem title="Events" icon="fa-calendar-alt">
                  {eventsDropdown.map((item, index) => (
                    <Link key={index} to={item.path} className="mobile-submenu-item" onClick={() => setMobileMenuOpen(false)}>
                      <i className={`fas ${item.icon}`}></i>
                      <div>
                        <div>{item.label}</div>
                        {item.description && <small>{item.description}</small>}
                      </div>
                    </Link>
                  ))}
                </AccordionItem>

                <Link to="/contact" className="mobile-menu-item" onClick={() => setMobileMenuOpen(false)}>
                  <i className="fas fa-envelope"></i>
                  <span>Contact Us</span>
                </Link>
                
                <div className="mobile-menu-footer">
                  <div className="mobile-contact-info">
                    <p><i className="fas fa-phone"></i> +234 905 584 6379</p>
                    <p><i className="fas fa-envelope"></i> info@nlngraclub.com</p>
                  </div>
                  <Link to="/login" className="btn btn-primary btn-block" onClick={() => setMobileMenuOpen(false)}>
                   Sign In / Register
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const AccordionItem = ({ title, icon, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="mobile-accordion-item">
      <button className="mobile-accordion-header" onClick={() => setIsOpen(!isOpen)}>
        <span><i className={`fas ${icon}`}></i>{title}</span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div className="mobile-accordion-content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}>
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ========== DOWNLOAD POPUP COMPONENT ==========
const DownloadPopup = ({ isOpen, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      const timer = setTimeout(() => {
        handleClose();
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = '/RA Club Member Onboarding Guide Rev 2.0.pdf';
    link.download = 'RA Club Member Onboarding Guide Rev 2.0.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    console.log('Onboarding guide downloaded');
    setTimeout(handleClose, 500);
  };

  if (!isOpen && !isVisible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="download-popup-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.75)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(6px)',
          }}
        >
          <motion.div
            className="download-popup-content"
            initial={{ scale: 0.8, y: 50, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.8, y: 50, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '24px',
              padding: '48px 40px',
              maxWidth: '480px',
              width: '90%',
              textAlign: 'center',
              position: 'relative',
              boxShadow: '0 32px 64px rgba(0,0,0,0.3)',
            }}
          >
            <button
              onClick={handleClose}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: '#f5f5f7',
                border: 'none',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#6e6e73',
                transition: 'all 0.2s',
              }}
            >
              <i className="fas fa-times"></i>
            </button>

            <div style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #28a745, #1e7e34)',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              boxShadow: '0 8px 24px rgba(40,167,69,0.3)',
            }}>
              <i className="fas fa-file-pdf" style={{ fontSize: '36px', color: 'white' }}></i>
            </div>

            <h3 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1d1d1f',
              marginBottom: '12px',
            }}>
              Welcome to NLNG RA Club!
            </h3>

            <p style={{
              fontSize: '16px',
              color: '#6e6e73',
              marginBottom: '28px',
              lineHeight: '1.5',
            }}>
              Get started with our comprehensive member onboarding guide.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              marginBottom: '28px',
              textAlign: 'left',
            }}>
              {[
                'Membership Benefits',
                'Facility Access Guide',
                'Club Rules & Regulations',
                'Section Information',
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  color: '#1d1d1f',
                  padding: '10px 12px',
                  background: '#f5f5f7',
                  borderRadius: '10px',
                }}>
                  <i className="fas fa-check-circle" style={{ color: '#28a745', fontSize: '14px', flexShrink: 0 }}></i>
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <button
              onClick={handleDownload}
              style={{
                width: '100%',
                padding: '16px',
                background: 'linear-gradient(135deg, #28a745, #1e7e34)',
                color: 'white',
                border: 'none',
                borderRadius: '14px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                transition: 'all 0.3s',
                boxShadow: '0 4px 16px rgba(40,167,69,0.3)',
                marginBottom: '16px',
              }}
            >
              <i className="fas fa-download"></i>
              Download Member Guide (PDF)
            </button>

            <p style={{
              fontSize: '12px',
              color: '#a1a1a6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              margin: 0,
            }}>
              <i className="fas fa-file-alt"></i>
        NLNG RA Club Member Onboarding Guide Rev 2.0
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ========== HERO SECTION WITH VIDEO (UPDATED POSTER) ==========
const HeroSection = () => {
  const scrollToAbout = () => scrollToSection('about-section');

  return (
    <section className="hero-section">
      <div className="hero-background">
        <video 
          autoPlay 
          muted 
          loop 
          playsInline 
          className="hero-video" 
          poster={getImagePath('raclub.jpg')}  // ← Changed this line only
        >
          <source src="https://nlngraclub.com/Boulevard.mp4" type="video/mp4" />
        </video>
        <div className="hero-overlay"></div>
      </div>
      
      <div className="container">
        <div className="hero-contentX">
          <motion.span className="hero-eyebrow" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            Welcome to
          </motion.span>
          <motion.h1
            className="hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <span className="text-white">NLNG RA</span>{" "}
            <span className="text-green">Club</span>
          </motion.h1>
          <motion.p
            className="hero-subtitle text-white"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Where community meets excellence. Experience premium living, world-class facilities, and a vibrant community.
          </motion.p>
           <motion.div
            className="hero-actions"
            style={{ marginTop: '40px' }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <button onClick={scrollToAbout} className="btn btn-white">
              Learn More
            </button>
          </motion.div>
        </div>
      </div>
      
      <motion.div className="scroll-indicator" animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2 }}
                  onClick={scrollToAbout}>
        <span className="scroll-text">Scroll to explore</span>
        <i className="fas fa-chevron-down"></i>
      </motion.div>
    </section>
  );
};
// ========== ABOUT SECTION ==========
const AboutSection = () => {
  const [activeTab, setActiveTab] = useState('story');
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  const tabs = [
    { id: 'story', label: 'Our Story' },
    { id: 'mission', label: 'Mission & Vision' },
    { id: 'values', label: 'Core Values' },
  ];

  const milestones = [
    { year: '1998', title: 'Founded', description: 'Club established' },
    { year: '2005', title: 'Expanded', description: 'Major renovation' },
    { year: '2015', title: 'Awarded', description: 'Best Club in Nigeria' },
    { year: '2024', title: 'Innovation', description: 'Digital transformation' },
  ];

  return (
    <section id="about-section" className="section about-section">
      <div className="container">
        <div className="section-header">
          <span className="section-eyebrow">About Us</span>
          <h2 className="section-title">A Legacy of <span className="text-green">Excellence</span></h2>
          <p className="section-subtitle">
            Since 1998, NLNG RA Club has been the heart of our community, providing a welcoming space where neighbors become friends.
          </p>
        </div>

        <div className="about-grid">
          <div className="about-content">
            <div className="about-tabs">
              {tabs.map(tab => (
                <button key={tab.id} className={`about-tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}>
                  {tab.label}
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div key={activeTab} className="tab-content"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.3 }}>
                {activeTab === 'story' && (
                  <div className="story-content">
                    <p className="lead-text">
                      Founded in 1998, NLNG RA Club began as a vision to create a vibrant community hub for residents.
                    </p>
                    <p>From humble beginnings, we've grown into Nigeria's premier residential club, offering world-class facilities and a thriving community of over 2,500 members.</p>
                    <div className="milestones">
                      {milestones.map((m, i) => (
                        <div key={i} className="milestone">
                          <span className="milestone-year">{m.year}</span>
                          <h4>{m.title}</h4>
                          <p>{m.description}</p>
                        </div>
                      ))}
                    </div>
                    <Link to="/our-story" className="text-link">Read our full story <i className="fas fa-arrow-right"></i></Link>
                  </div>
                )}
                {activeTab === 'mission' && (
                  <div className="mission-content">
                    <h3>Our Mission</h3>
                    <p>The objective of The Club is to provide recreational, social and cultural activities for its members and their guests in order to stimulate social integration, intercultural relationship and friendship within RA community.</p>
                    <h3 className="mt-4">Our Vision for the Future</h3>
                    <p>As the club moves forward, it remains committed to its mission of enhancing the quality of life for NLNG Residential Area residents. Guided by a spirit of innovation and inclusivity, the RA Club aspires to strengthen bonds among members, set new benchmarks for excellence, and maintain its reputation as a world-class community organization.</p>
                    <p>The NLNG RA Club is a testament to what a united community can achieve. It stands proud not only as a space for relaxation and enjoyment but as a beacon of collaboration, growth, and shared purpose.</p>
                  </div>
                )}
                {activeTab === 'values' && (
                  <div className="values-content">
                    <div className="values-grid">
                      {[
                        { icon: 'fa-users', title: 'Community', desc: 'Building lasting relationships' },
                        { icon: 'fa-star', title: 'Excellence', desc: 'Delivering highest standards' },
                        { icon: 'fa-heart', title: 'Wellness', desc: 'Promoting healthy lifestyles' },
                        { icon: 'fa-leaf', title: 'Sustainability', desc: 'Responsible practices' },
                      ].map((v, i) => (
                        <div key={i} className="value-card">
                          <i className={`fas ${v.icon} value-icon`}></i>
                          <h4>{v.title}</h4>
                          <p>{v.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="about-media">
            <div className="media-container">
              <img src={getImagePath('images/company/company-image.jpg')} alt="Club" />
              <div className="media-overlay">
                <button className="play-button" onClick={() => setVideoModalOpen(true)}>
                  <i className="fas fa-play"></i>
                </button>
                <span>Watch our story</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {videoModalOpen && (
          <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      onClick={() => setVideoModalOpen(false)}>
            <motion.div className="modal-content video-modal" initial={{ scale: 0.8 }} animate={{ scale: 1 }} exit={{ scale: 0.8 }}
                        onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setVideoModalOpen(false)}>
                <i className="fas fa-times"></i>
              </button>
              <div className="video-container">
                <video controls autoPlay>
                  <source src="https://nlngraclub.com/Boulevard.mp4" type="video/mp4" />
                </video>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

// ========== LIFESTYLE SECTION ==========
const LifestyleSection = () => {
  const features = [
    {
      title: 'Premium Sports Facilities',
      description: 'State-of-the-art tennis courts, squash courts, golf course, and fully equipped gym.',
      icon: 'fa-basketball-ball',
      image: getImagePath('images/company/sports-hall-complex.jpg')
    },
    {
      title: 'Recreation & Leisure',
      description: 'Olympic-size swimming pool, children\'s playground, and scenic picnic areas.',
      icon: 'fa-swimmer',
      image: getImagePath('images/bars/swimming-pool.jpg')
    },
    {
      title: 'Dining & Social Spaces',
      description: 'Elegant clubhouse restaurant, bars, and versatile event venues.',
      icon: 'fa-utensils',
      image: getImagePath('images/bars/abuja-boulevard.jpg')
    },
    {
      title: 'Wellness & Spa',
      description: 'Holistic wellness center with spa treatments, sauna, and yoga studio.',
      icon: 'fa-spa',
      image: getImagePath('images/company/DSC00122.jpg')
    }
  ];

  const [selectedFeature, setSelectedFeature] = useState(0);

  return (
    <section className="section lifestyle-section">
      <div className="container">
        <div className="section-header">
          <span className="section-eyebrow">Our Lifestyle</span>
          <h2 className="section-title">Experience <span className="text-green">Premium Living</span></h2>
          <p className="section-subtitle">Discover a world of amenities designed to enhance every aspect of your life.</p>
        </div>

        <div className="lifestyle-grid">
          <div className="lifestyle-content">
            <div className="features-list">
              {features.map((feature, i) => (
                <motion.div key={i} className={`feature-item ${selectedFeature === i ? 'active' : ''}`}
                            onClick={() => setSelectedFeature(i)} whileHover={{ x: 10 }}>
                  <div className="feature-icon"><i className={`fas ${feature.icon}`}></i></div>
                  <div className="feature-text">
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="lifestyle-cta">
              <Link to="/facilities" className="btn btn-primary">Explore All Facilities <i className="fas fa-arrow-right"></i></Link>
            </div>
          </div>

          <div className="lifestyle-image">
            <AnimatePresence mode="wait">
              <motion.img key={selectedFeature} src={features[selectedFeature].image}
                          alt={features[selectedFeature].title}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          transition={{ duration: 0.3 }} />
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

// ========== TESTIMONIALS SECTION ==========
const TestimonialsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  
  const testimonials = [
    {
      id: 1,
      name: 'Michael Ogbuanu',
      role: 'President',
      content: 'Leading the club with vision and dedication has been an honor. The NLNG RA Club represents community excellence at its finest, and I am proud to serve our members daily.',
      rating: 5,
      image: getImagePath('iconX.png'),
      membership: 'Club President',
      icon: 'fa-crown',
      imageFilter: 'brightness(1) sepia(0.3) hue-rotate(0deg)'
    },
    {
      id: 2,
      name: 'Chinedu Onyia',
      role: 'Vice President',
      content: 'I am committed to supporting the president and ensuring smooth club operations. Together, we are building a legacy of excellence for future generations.',
      rating: 5,
      image: getImagePath('iconX.png'),
      membership: 'Vice President',
      icon: 'fa-handshake',
      imageFilter: 'brightness(1.1) sepia(0.5) hue-rotate(30deg)'
    },
    {
      id: 3,
      name: 'Linda Ogbu',
      role: 'Secretary',
      content: 'Managing club records and communications with precision is my passion. Every document, every meeting, every detail matters in serving our distinguished members.',
      rating: 5,
      image: getImagePath('iconX.png'),
      membership: 'Club Secretary',
      icon: 'fa-file-alt',
      imageFilter: 'brightness(0.9) saturate(1.5) hue-rotate(200deg)'
    },
    {
      id: 4,
      name: 'Ifeoma Willie-Pepple',
      role: 'Treasurer',
      content: 'Overseeing club finances with transparency and strategic planning ensures sustainable growth. Every naira is accounted for to maximize member benefits.',
      rating: 5,
      image: getImagePath('iconX.png'),
      membership: 'Club Treasurer',
      icon: 'fa-chart-line',
      imageFilter: 'brightness(1.05) sepia(0.4) hue-rotate(280deg)'
    },
    {
      id: 5,
      name: 'Chima Tasieobi',
      role: 'Sports Member',
      content: 'Driving sports activities and promoting athletic excellence is my mission. From tennis to swimming, we are building champions at NLNG RA Club.',
      rating: 5,
      image: getImagePath('iconX.png'),
      membership: 'Sports Committee',
      icon: 'fa-futbol',
      imageFilter: 'brightness(1.15) sepia(0.2) hue-rotate(60deg)'
    },
    {
      id: 6,
      name: 'Grace Ikufisile',
      role: 'Cultural Member',
      content: 'Enriching club life through cultural events and activities brings me joy. Our heritage and traditions make this club truly special and unique.',
      rating: 5,
      image: getImagePath('iconX.png'),
      membership: 'Cultural Committee',
      icon: 'fa-music',
      imageFilter: 'brightness(1) saturate(1.3) hue-rotate(320deg)'
    },
    {
      id: 7,
      name: 'Buhari Bello',
      role: 'Technical Member',
      content: 'Ensuring technical excellence and facility maintenance is my priority. Modern equipment and pristine facilities define the NLNG RA Club experience.',
      rating: 5,
      image: getImagePath('iconX.png'),
      membership: 'Technical Committee',
      icon: 'fa-cogs',
      imageFilter: 'brightness(0.95) sepia(0.6) hue-rotate(150deg)'
    },
    {
      id: 8,
      name: 'Emiel Ten Velde',
      role: 'General Member (Expatriate)',
      content: 'Bringing international perspective to club development has been rewarding. NLNG RA Club rivals the best clubs in Europe with its world-class facilities.',
      rating: 5,
      image: getImagePath('iconX.png'),
      membership: 'Expatriate Member',
      icon: 'fa-globe',
      imageFilter: 'brightness(1.08) saturate(1.2) hue-rotate(90deg)'
    },
    {
      id: 9,
      name: 'Osademe Azubike',
      role: 'Social Member',
      content: 'Organizing social events and fostering community connections is my calling. Watch out for our upcoming galas, parties, and family fun days!',
      rating: 5,
      image: getImagePath('iconX.png'),
      membership: 'Social Committee',
      icon: 'fa-glass-cheers',
      imageFilter: 'brightness(1.12) sepia(0.3) hue-rotate(50deg)'
    }
  ];

  const nextSlide = () => {
    setDirection(1);
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setDirection(-1);
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index) => {
    setDirection(index > activeIndex ? 1 : -1);
    setActiveIndex(index);
  };

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.8
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
      scale: 0.8
    })
  };

  return (
    <section className="testimonials-section dark-bg">
      <div className="container">
        <div className="section-header">
          <span className="section-eyebrow dark">Our Leadership</span>
          <h2 className="section-title light">Voices of <span className="text-green-light">Our Leaders</span></h2>
          <p className="section-subtitle light">Hear from the dedicated committee members who make NLNG RA Club exceptional.</p>
        </div>

        <div className="testimonials-wrapper">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={activeIndex}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.3 },
                scale: { duration: 0.3 }
              }}
              className="testimonial-main"
            >
              <div className="testimonial-card-large dark-card">
                <div className="testimonial-content-wrapper">
                  <div className="quote-icon-wrapper">
                    <i className="fas fa-quote-left"></i>
                  </div>
                  <p className="testimonial-text-large dark-text">"{testimonials[activeIndex].content}"</p>
                  <div className="testimonial-stars">
                    {[...Array(testimonials[activeIndex].rating)].map((_, i) => (
                      <i key={i} className="fas fa-star"></i>
                    ))}
                  </div>
                  <div className="testimonial-author-large">
                    <div className="author-avatar">
                      <img 
                        src={testimonials[activeIndex].image} 
                        alt={testimonials[activeIndex].name}
                        style={{ filter: testimonials[activeIndex].imageFilter }}
                      />
                    </div>
                    <div className="author-info">
                      <h4 className="dark-name">{testimonials[activeIndex].name}</h4>
                      <p className="author-role dark-role">
                        <i className={`fas ${testimonials[activeIndex].icon}`}></i> {testimonials[activeIndex].role}
                      </p>
                      <div className="author-badge dark-badge">
                        <i className="fas fa-id-card"></i>
                        <span>{testimonials[activeIndex].membership}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <button className="testimonial-nav dark-nav prev" onClick={prevSlide}>
            <i className="fas fa-chevron-left"></i>
          </button>
          <button className="testimonial-nav dark-nav next" onClick={nextSlide}>
            <i className="fas fa-chevron-right"></i>
          </button>

          <div className="testimonial-dots dark-dots">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`dot dark-dot ${activeIndex === index ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
              />
            ))}
          </div>
        </div>

        <div className="testimonial-thumbnails dark-thumbnails">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              className={`thumbnail dark-thumbnail ${activeIndex === index ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <img 
                src={testimonial.image} 
                alt={testimonial.name}
                style={{ filter: testimonial.imageFilter }}
              />
              <div className="thumbnail-overlay dark-overlay">
                <i className="fas fa-play"></i>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
// ========== UPCOMING EVENTS SECTION WITH TABS ==========
const UpcomingEventsSection = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming' or 'past'

  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    if (imageUrl.startsWith('/nlnapi/') || imageUrl.startsWith('/uploads/')) {
      return `https://nlngraclub.com${imageUrl}`;
    }
    if (imageUrl.startsWith('/')) {
      return `https://nlngraclub.com${imageUrl}`;
    }
    return `https://nlngraclub.com/${imageUrl}`;
  };

  const getFallbackImage = (eventType) => {
    const fallbacks = {
      'Sports': 'https://nlngraclub.com/images/company/sports-hall-complex.jpg',
      'Social': 'https://nlngraclub.com/images/company/xmas.jpg',
      'Party': 'https://nlngraclub.com/images/company/newyear.jpg',
      'Wellness': 'https://nlngraclub.com/images/company/wellness.jpg',
      'Family': 'https://nlngraclub.com/images/company/fun.PNG',
      'Entertainment': 'https://nlngraclub.com/images/company/company-image-3.jpg',
      'Dining': 'https://nlngraclub.com/images/bars/abuja-boulevard.jpg',
      'club': 'https://nlngraclub.com/images/company/company-image.jpg',
      'default': 'https://nlngraclub.com/images/company/company-image.jpg'
    };
    return fallbacks[eventType] || fallbacks.default;
  };

  const handleImageError = (eventId, eventType) => {
    if (!imageErrors[eventId]) {
      setImageErrors(prev => ({ ...prev, [eventId]: true }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return { day: 'TBD', month: 'TBD', year: 'TBD', fullDate: 'Date TBA' };
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return { day: 'TBD', month: 'TBD', year: 'TBD', fullDate: 'Date TBA' };
      return {
        day: date.getDate(),
        month: date.toLocaleString('default', { month: 'short' }),
        year: date.getFullYear(),
        fullDate: date.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      };
    } catch (e) {
      return { day: 'TBD', month: 'TBD', year: 'TBD', fullDate: 'Date TBA' };
    }
  };

  const handleReadMore = (event) => {
    setSelectedEvent(event);
    setShowModal(true);
  };

  // ✅ FIXED: using eventService instead of direct fetch to index.php
  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        // eventService.getEvents(upcoming, limit)
        // passing 'false' gets all events (both upcoming and past)
        const result = await eventService.getEvents(false, 200);
        if (result.success && result.events) {
          setEvents(result.events);
        } else {
          setEvents([]);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const getEventImage = (event) => {
    if (imageErrors[event.id]) {
      return getFallbackImage(event.event_type);
    }
    if (event.image_url) {
      return getFullImageUrl(event.image_url);
    }
    return getFallbackImage(event.event_type);
  };

  const isUpcoming = (eventDate) => {
    if (!eventDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDateObj = new Date(eventDate);
    eventDateObj.setHours(0, 0, 0, 0);
    return eventDateObj >= today;
  };

  // Separate events into upcoming and past
  const upcomingEvents = events.filter(event => isUpcoming(event.event_date));
  const pastEvents = events.filter(event => !isUpcoming(event.event_date));

  // Sort upcoming events by date (earliest first)
  const sortedUpcomingEvents = [...upcomingEvents].sort((a, b) => {
    const dateA = a.event_date ? new Date(a.event_date) : new Date(8640000000000000);
    const dateB = b.event_date ? new Date(b.event_date) : new Date(8640000000000000);
    return dateA - dateB;
  });

  // Sort past events by date (most recent first)
  const sortedPastEvents = [...pastEvents].sort((a, b) => {
    const dateA = a.event_date ? new Date(a.event_date) : new Date(0);
    const dateB = b.event_date ? new Date(b.event_date) : new Date(0);
    return dateB - dateA;
  });

  const renderEventCard = (event, isPast = false) => {
    const { day, month, year } = formatDate(event.event_date);
    const imageToShow = getEventImage(event);
    
    return (
      <div key={event.id} className={`event-card ${isPast ? 'past-event' : ''}`}>
        <div className="event-image">
          <img 
            src={imageToShow} 
            alt={event.title}
            onError={() => handleImageError(event.id, event.event_type)}
          />
          <div className={`event-category ${isPast ? 'past' : ''}`}>
            {isPast ? 'Past Event' : (event.event_type || 'Event')}
          </div>
        </div>
        <div className="event-content">
          <div className="event-meta">
            <span className="event-date">
              <i className="far fa-calendar-alt"></i> {month} {day}, {year}
            </span>
            <span className="event-author">
              <i className="far fa-user"></i> Club Team
            </span>
          </div>
          <h3 className="event-title">{event.title}</h3>
          <div className="event-details">
            <span><i className="fas fa-clock"></i> {event.event_time}</span>
            <span><i className="fas fa-map-marker-alt"></i> {event.location}</span>
          </div>
          <p className="event-excerpt">
            {event.description && event.description.length > 100 
              ? `${event.description.substring(0, 100)}...` 
              : event.description}
          </p>
          <button onClick={() => handleReadMore(event)} className="event-read-more">
            {isPast ? 'View Details' : 'Read More'} <i className="fas fa-arrow-right"></i>
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <section className="section upcoming-events-section">
        <div className="container">
          <div className="section-header">
            <span className="section-eyebrow">Stay Connected</span>
            <h2 className="section-title">Club <span className="text-green">Events</span></h2>
            <p className="section-subtitle">Discover all events and activities happening at the club</p>
          </div>
          <div className="loading-container" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="spinner">
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '48px', color: '#2e7d32' }}></i>
            </div>
            <p style={{ marginTop: '20px', color: '#666' }}>Loading events...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section upcoming-events-section">
      <div className="container">
        <div className="section-header">
          <span className="section-eyebrow">Stay Connected</span>
          <h2 className="section-title">Club <span className="text-green">Events</span></h2>
          <p className="section-subtitle">Discover all events and activities happening at the club</p>
        </div>

        {events.length === 0 ? (
          <div className="empty-state" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <i className="fas fa-calendar-times" style={{ fontSize: '48px', color: '#999', marginBottom: '20px' }}></i>
            <p style={{ fontSize: '18px', color: '#333' }}>No events found at this time.</p>
            <p style={{ fontSize: '14px', color: '#666' }}>Check back soon for new events!</p>
          </div>
        ) : (
          <>
            {/* TABS */}
            <div className="events-tabs">
              <button 
                className={`tab-btn ${activeTab === 'upcoming' ? 'active' : ''}`}
                onClick={() => setActiveTab('upcoming')}
              >
                <i className="fas fa-calendar-alt"></i>
                Upcoming Events
                <span className="tab-count">{sortedUpcomingEvents.length}</span>
              </button>
              <button 
                className={`tab-btn ${activeTab === 'past' ? 'active' : ''}`}
                onClick={() => setActiveTab('past')}
              >
                <i className="fas fa-history"></i>
                Past Events
                <span className="tab-count">{sortedPastEvents.length}</span>
              </button>
            </div>

            {/* UPCOMING EVENTS PANEL - NOW USING GRID INSTEAD OF SWIPER */}
            {activeTab === 'upcoming' && (
              <div className="events-panel upcoming-panel">
                {sortedUpcomingEvents.length === 0 ? (
                  <div className="empty-tab-state">
                    <i className="fas fa-calendar-check"></i>
                    <p>No upcoming events scheduled at this time.</p>
                    <p className="small-text">Check back soon for exciting events!</p>
                  </div>
                ) : (
                  <div className="upcoming-events-grid">
                    {sortedUpcomingEvents.map((event) => renderEventCard(event, false))}
                  </div>
                )}
              </div>
            )}

            {/* PAST EVENTS PANEL */}
            {activeTab === 'past' && (
              <div className="events-panel past-panel">
                {sortedPastEvents.length === 0 ? (
                  <div className="empty-tab-state">
                    <i className="fas fa-calendar-week"></i>
                    <p>No past events to display.</p>
                    <p className="small-text">Events you've attended will appear here.</p>
                  </div>
                ) : (
                  <div className="past-events-grid">
                    {sortedPastEvents.map((event) => renderEventCard(event, true))}
                  </div>
                )}
              </div>
            )}

            <div className="events-cta">
              <Link to="/calendar" className="btn btn-primary">
                View Full Calendar <i className="fas fa-calendar-alt"></i>
              </Link>
            </div>
          </>
        )}
      </div>

      {/* EVENT MODAL - same as before */}
      <AnimatePresence>
        {showModal && selectedEvent && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.85)',
              zIndex: 99998,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(8px)',
              padding: '20px',
            }}
          >
            <motion.div 
              initial={{ scale: 0.85, y: 30, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.85, y: 30, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={e => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '28px',
                width: '100%',
                maxWidth: '650px',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 32px 64px rgba(0,0,0,0.3)',
                position: 'relative',
              }}
            >
              <div style={{
                position: 'sticky',
                top: 0,
                background: 'white',
                borderRadius: '28px 28px 0 0',
                zIndex: 10,
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '20px 24px',
                  borderBottom: '1px solid #e5e5e7',
                }}>
                  <h3 style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1d1d1f',
                    margin: 0,
                    flex: 1,
                  }}>
                    {selectedEvent.title}
                  </h3>
                  <button
                    onClick={() => setShowModal(false)}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      background: '#f5f5f7',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: '16px',
                      color: '#6e6e73',
                      transition: 'all 0.2s',
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              </div>

              <div style={{ padding: '0' }}>
                <div style={{
                  width: '100%',
                  height: '280px',
                  overflow: 'hidden',
                  background: '#f5f5f7',
                }}>
                  <img
                    src={getEventImage(selectedEvent)}
                    alt={selectedEvent.title}
                    onError={() => handleImageError(selectedEvent.id, selectedEvent.event_type)}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </div>

                <div style={{ padding: '28px' }}>
                  <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 14px',
                      background: selectedEvent.event_date && isUpcoming(selectedEvent.event_date) 
                        ? 'rgba(40,167,69,0.12)' 
                        : 'rgba(108,117,125,0.12)',
                      color: selectedEvent.event_date && isUpcoming(selectedEvent.event_date) ? '#28a745' : '#6c757d',
                      borderRadius: '30px',
                      fontSize: '12px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                    }}>
                      <i className={`fas ${selectedEvent.event_date && isUpcoming(selectedEvent.event_date) ? 'fa-calendar-day' : 'fa-calendar-check'}`}></i>
                      {selectedEvent.event_date && isUpcoming(selectedEvent.event_date) ? 'Upcoming' : 'Past Event'}
                    </span>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '14px',
                    marginBottom: '28px',
                    padding: '20px',
                    background: '#f8f9fa',
                    borderRadius: '20px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '36px', height: '36px', background: '#e8f5e9', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className="fas fa-calendar-alt" style={{ color: '#28a745' }}></i>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#999' }}>DATE</div>
                        <div style={{ fontWeight: '500' }}>{formatDate(selectedEvent.event_date).fullDate}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '36px', height: '36px', background: '#e8f5e9', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className="fas fa-clock" style={{ color: '#28a745' }}></i>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#999' }}>TIME</div>
                        <div style={{ fontWeight: '500' }}>{selectedEvent.event_time}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '36px', height: '36px', background: '#e8f5e9', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className="fas fa-map-marker-alt" style={{ color: '#28a745' }}></i>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#999' }}>LOCATION</div>
                        <div style={{ fontWeight: '500' }}>{selectedEvent.location}</div>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: '28px' }}>
                    <h4 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '14px' }}>
                      <i className="fas fa-info-circle" style={{ color: '#28a745', marginRight: '8px' }}></i>
                      About This Event
                    </h4>
                    <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#6e6e73' }}>
                      {selectedEvent.description}
                    </p>
                  </div>

                  <button
                    onClick={() => setShowModal(false)}
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: 'linear-gradient(135deg, #28a745, #1e7e34)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '16px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    <i className="fas fa-check-circle"></i> Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
// ========== FOOTER - 4 COLUMNS RESPONSIVE ==========
const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    setSubscribed(true);
    setEmail('');
    setTimeout(() => setSubscribed(false), 3000);
  };

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-newsletter">
          <div className="newsletter-content">
            <h3>Stay Updated</h3>
            <p>Subscribe to our newsletter for exclusive offers and event updates.</p>
          </div>
          <form className="newsletter-form" onSubmit={handleSubscribe}>
            <div className="input-group">
              <input type="email" placeholder="Your email address" value={email}
                     onChange={(e) => setEmail(e.target.value)} required />
              <button type="submit" className="btn btn-primary">Subscribe</button>
            </div>
            {subscribed && (
              <motion.div className="success-message" initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <i className="fas fa-check-circle"></i> Thank you for subscribing!
              </motion.div>
            )}
          </form>
        </div>

        <div className="footer-top">
          <div className="footer-grid">
            {/* Column 1 - Logo & Social */}
            <div className="footer-column">
              <img src={getImagePath('images/logo.png')} alt="NLNG RA Club" className="footer-logo" />
              <p className="footer-description">Nigeria's premier residential club offering unparalleled lifestyle experiences since 1995.</p>
              <div className="footer-social">
                <a href="#" className="social-link" aria-label="Facebook"><i className="fab fa-facebook-f"></i></a>
                <a href="#" className="social-link" aria-label="Twitter"><i className="fab fa-twitter"></i></a>
                <a href="#" className="social-link" aria-label="Instagram"><i className="fab fa-instagram"></i></a>
                <a href="#" className="social-link" aria-label="LinkedIn"><i className="fab fa-linkedin-in"></i></a>
                <a href="#" className="social-link" aria-label="YouTube"><i className="fab fa-youtube"></i></a>
              </div>
            </div>

            {/* Column 2 - The Club */}
            <div className="footer-column">
              <h3>The Club</h3>
              <ul className="footer-links">
                <li><Link to="/our-story">Our Story</Link></li>
                <li><Link to="/leadership">Leadership</Link></li>
                <li><Link to="/policies">Policies</Link></li>
                <li><Link to="/history">Club History</Link></li>
                <li><Link to="/awards">Awards</Link></li>
              </ul>
            </div>

            {/* Column 3 - Quick Links */}
            <div className="footer-column">
              <h3>Quick Links</h3>
              <ul className="footer-links">
                <li><Link to="/events">Upcoming Events</Link></li>
                <li><Link to="/calendar">Event Calendar</Link></li>
                <li><Link to="/gallery">Photo Gallery</Link></li>
                <li><Link to="/facilities">Our Facilities</Link></li>
                <li><Link to="/sections">Club Sections</Link></li>
              </ul>
            </div>

            {/* Column 4 - Contact Info */}
            <div className="footer-column">
              <h3>Contact Us</h3>
              <ul className="footer-contact">
                <li>
                  <i className="fas fa-map-marker-alt"></i>
                  <span>NLNG Residential Area, Finima, Rivers State, Nigeria</span>
                </li>
                <li>
                  <i className="fas fa-phone"></i>
                  <a href="tel:+2347036366115">+234 703 636 6115</a>
                </li>
                <li>
                  <i className="fas fa-envelope"></i>
                  <a href="mailto:info@nlngraclub.com">info@nlngraclub.com</a>
                </li>
                <li>
                  <i className="fas fa-clock"></i>
                  <span>Mon - Sun: 8:00 AM - 10:00 PM</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <p>&copy; {new Date().getFullYear()} NLNG RA Club. All rights reserved.</p>
            <div className="footer-bottom-links">
              <Link to="/privacy">Privacy Policy</Link>
              <span className="separator">|</span>
              <Link to="/terms">Terms of Service</Link>
              <span className="separator">|</span>
              <Link to="/sitemap">Sitemap</Link>
            </div>
          </div>
          <div className="footer-security">
            <img src="https://shield.sitelock.com/shield/nlngraclub.com" alt="SiteLock" className="security-badge" />
          </div>
        </div>
      </div>
    </footer>
  );
};

// ========== SCROLL TO TOP ==========
const ScrollToTop = () => {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const winHeight = document.documentElement.scrollHeight - window.innerHeight;
      setVisible(scrollY > 500);
      setProgress((scrollY / winHeight) * 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <motion.button className={`scroll-to-top ${visible ? 'visible' : ''}`} onClick={scrollToTop}
                   initial={{ opacity: 0, scale: 0.8 }}
                   animate={{ opacity: visible ? 1 : 0, scale: visible ? 1 : 0.8 }}
                   whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
      <svg className="progress-ring" width="50" height="50">
        <circle className="progress-ring__circle" stroke="white" strokeWidth="2" fill="transparent"
                r="20" cx="25" cy="25"
                style={{ strokeDasharray: `${2 * Math.PI * 20}`,
                         strokeDashoffset: `${2 * Math.PI * 20 * (1 - progress / 100)}` }} />
      </svg>
      <i className="fas fa-arrow-up"></i>
    </motion.button>
  );
};

// ========== BLOG PAGE COMPONENT ==========
const BlogPage = () => (
  <div className="page">
    <div className="page-hero">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span className="eyebrow">Stay Connected</span>
          <h1>Blog & Updates</h1>
          <p className="section-sub">News, events, and stories from our vibrant community</p>
        </motion.div>
      </div>
    </div>
    <section className="section">
      <div className="container">
        <div className="blog-grid">
          <p>Blog posts coming soon...</p>
        </div>
      </div>
    </section>
  </div>
);

// ========== HOME PAGE WITH DOWNLOAD POPUP AS FIRST MODAL ==========
const HomePage = () => {
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    // Always show popup on every visit after a short delay
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const handlePopupClose = () => {
    setShowPopup(false);
  };

  return (
    <div className="home-page">
      <HeroSection />
      <AboutSection />
      <LifestyleSection />
      <TestimonialsSection />
      <UpcomingEventsSection />
      {/* Download popup renders on top of everything as the first modal */}
      <DownloadPopup isOpen={showPopup} onClose={handlePopupClose} />
    </div>
  );
};

// Facilities Page Wrapper
const FacilitiesPageWrapper = () => {
  const equipmentByCategory = (equipmentCatalog || []).reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <div className="page">
      <div className="page-header">
        <div className="container">
          <h1>Club Facilities</h1>
          <p>Explore our world-class amenities</p>
        </div>
      </div>
      <FacilitiesPage 
        facilities={facilities || []}
        equipmentByCategory={equipmentByCategory}
        user={null}
        addNotification={() => {}}
        addAuditLog={() => {}}
        generateBookingId={() => `RA-${Date.now()}`}
      />
    </div>
  );
};

// Sections Page Wrapper
const SectionsPageWrapper = () => (
  <div className="page">
    <div className="page-header"><div className="container"><h1>Club Sections</h1><p>Join our specialized sports clubs</p></div></div>
    <SectionsPage 
      sections={sections || []}
      user={null}
      addNotification={() => {}}
    />
  </div>
);

// ========== MAIN APP ==========
const App = () => {
  const location = useLocation();
  
const hiddenRoutes = [
  '/dashboard', '/login', '/register', '/forgot-password', '/admin',
  '/booking', '/my-bookings', '/profile', '/settings', '/notifications',
  '/join-section', '/equipment', '/approved-audit'  // ← Add this
];
  const isMainFacilitiesPage = location.pathname === '/facilities';
  const isMainSectionsPage = location.pathname === '/sections';
  const isAuditPage = location.pathname === '/approved-audit';
  const isHiddenRoute = hiddenRoutes.some(route => location.pathname === route || location.pathname.startsWith(route + '/'));
  const shouldHideNavbar = isHiddenRoute || isMainFacilitiesPage || isMainSectionsPage || isAuditPage;

  return (
    <div className="app">
      {!shouldHideNavbar && <Navbar />}
      
      <main className="main-content">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/our-story" element={<OurStory />} />
          <Route path="/leadership" element={<Leadership />} />
          <Route path="/membership-eligibility" element={<MembershipEligibility />} />
          <Route path="/history" element={<ClubHistory />} />
          <Route path="/awards" element={<Awards />} />
          <Route path="/policies" element={<Policies />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/events" element={<UpcomingEvents />} />
          <Route path="/calendar" element={<EventCalendar />} />
          <Route path="/gallery" element={<PhotoGallery />} />
          <Route path="/past-events" element={<PastEvents />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:id" element={<BlogPage />} />
          <Route path="/virtual-tour" element={<FacilitiesPageWrapper />} />
          <Route path="/reservation" element={<BlogPage />} />
          <Route path="/faq" element={<ContactPage />} />
          <Route path="/admin/approvals" element={<AdminApprovalPage />} />
          <Route path="/equipment" element={<ProtectedRoute><EquipmentPage /></ProtectedRoute>} />
          <Route path="/my-bookings" element={<ProtectedRoute><MyBookingsPage /></ProtectedRoute>} />
          <Route path="/booking/:id" element={<ProtectedRoute><BookingDetailPage /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/booking/*" element={<ProtectedRoute><BookingPage /></ProtectedRoute>} />
          <Route path="/facilities" element={<FacilitiesPageWrapper />} />
          <Route path="/facilities/abuja-boulevard" element={<AbujaBoulevard />} />
          <Route path="/facilities/banquet-hall" element={<BanquetHall />} />
          <Route path="/facilities/clubhouse-alcove" element={<ClubhouseAlcove />} />
          <Route path="/facilities/clubhouse-west-bar" element={<ClubhouseWestBar />} />
          <Route path="/facilities/dbn-bar" element={<DBNBar />} />
          <Route path="/facilities/finima-nature-park" element={<FinimaNaturePark />} />
          <Route path="/facilities/golf-course-bar" element={<GolfCourseBar />} />
          <Route path="/facilities/hippo-bar" element={<HippoBar />} />
          <Route path="/facilities/marquee-tent" element={<MarqueeTent />} />
          <Route path="/facilities/market-square" element={<MarketSquare />} />
          <Route path="/facilities/sports-hall-complex" element={<SportsHallComplex />} />
          <Route path="/facilities/swimming-pool" element={<SwimmingPool />} />
          <Route path="/facilities/tennis-hut" element={<TennisHut />} />
          <Route path="/facilities/wellness-center" element={<WellnessCenter />} />
          <Route path="/facilities/kids-play-area" element={<KidsPlayArea />} />
          <Route path="/sections" element={<SectionsPageWrapper />} />
          <Route path="/sections/aerobics" element={<AthleticsAerobics />} />
          <Route path="/sections/badminton" element={<Badminton />} />
          <Route path="/sections/ballet-gymnastics" element={<BalletGymnastics />} />
          <Route path="/sections/basketball" element={<Basketball />} />
          <Route path="/sections/chess" element={<Chess />} />
          <Route path="/sections/football" element={<Football />} />
          <Route path="/sections/golf" element={<Golf />} />
          <Route path="/sections/karate" element={<Karate />} />
          <Route path="/sections/pool-snooker" element={<PoolSnooker />} />
          <Route path="/sections/rala" element={<RALA />} />
          <Route path="/sections/scrabble" element={<Scrabble />} />
          <Route path="/sections/squash" element={<Squash />} />
          <Route path="/sections/swimming" element={<Swimming />} />
          <Route path="/sections/table-tennis" element={<TableTennis />} />
          <Route path="/sections/tennis" element={<Tennis />} />
          <Route path="/sections/volleyball" element={<Volleyball />} />
          <Route path="/sections/yoga" element={<Yoga />} />
          <Route path="/sections/cycling" element={<Cycling />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/resend-activation" element={<ResendActivation />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/parent-approvals" element={<ParentApprovalDashboard />} />
          <Route path="/elections" element={<Election2026 />} />
          <Route path="/approved-audit" element={<ApprovedBookingsAudit />} />
        </Routes>
      </main>
      
      {!shouldHideNavbar && <Footer />}
      {!shouldHideNavbar && (
        <>
          <VisitorCounter compact={true} position="bottom-left" offsetX={24} />
        </>
      )}
      <ScrollToTop />
    </div>
  );
};

export default App;
